const isBrowser = typeof window !== 'undefined';
const isDocument = typeof document !== 'undefined';
const GOOGLE_CLICK_CAPTURE_VERSION = 1;
const GOOGLE_CLICK_CAPTURE_SOURCES = new Set(['url_param', 'google_cookie']);
const GOOGLE_CLICK_IDENTIFIER_TYPES = new Set(['gclid', 'gbraid', 'wbraid']);
const MAX_GOOGLE_CLICK_CANDIDATES = 20;
const MAX_GOOGLE_CLICK_IDENTIFIER_LENGTH = 512;
const MAX_GOOGLE_CLICK_SOURCE_LENGTH = 40;

export function extractGclidFromCookieValue(raw) {
  return extractGoogleClickFromCookieValue(raw).gclid;
}

export function extractGoogleClickFromCookieValue(raw) {
  if (!raw) return { gclid: '', capturedAt: '' };
  const value = String(raw).trim();
  if (!value) return { gclid: '', capturedAt: '' };
  const segments = value.split('.');
  if (segments.length >= 3) {
    const candidate = segments[segments.length - 1];
    const rawTimestamp = segments[segments.length - 2];
    const timestampSeconds = /^\d+$/.test(rawTimestamp) ? Number(rawTimestamp) : NaN;
    const timestamp = new Date(timestampSeconds * 1000);
    const capturedAt = Number.isSafeInteger(timestampSeconds)
      && timestampSeconds > 0
      && Number.isFinite(timestamp.getTime())
      ? timestamp.toISOString()
      : '';
    return {
      gclid: candidate ? candidate.trim() : '',
      capturedAt,
    };
  }
  return { gclid: value, capturedAt: '' };
}

export function extractGaClientIdFromCookieValue(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  const segments = value.split('.');
  if (
    segments.length >= 4 &&
    /^GA\d+$/i.test(segments[0]) &&
    /^\d+$/.test(segments[segments.length - 2]) &&
    /^\d+$/.test(segments[segments.length - 1])
  ) {
    return `${segments[segments.length - 2]}.${segments[segments.length - 1]}`;
  }
  return /^\d+\.\d+$/.test(value) ? value : '';
}

export function readGoogleClickFromCookies() {
  if (!isDocument) return { gclid: '', capturedAt: '' };
  try {
    const cookieSource = document.cookie || '';
    if (!cookieSource) return '';
    const entries = cookieSource.split(';');
    for (const entry of entries) {
      const pair = entry.trim();
      if (!pair) continue;
      const [key, ...rest] = pair.split('=');
      if (!key || rest.length === 0) continue;
      const lowerKey = key.trim().toLowerCase();
      if (lowerKey === 'gclid') {
        const decoded = decodeURIComponent(rest.join('='));
        const candidate = decoded.trim();
        if (candidate) return { gclid: candidate, capturedAt: '' };
      }
      if (lowerKey === '_gcl_aw' || lowerKey === '_gcl_dc') {
        const decoded = decodeURIComponent(rest.join('='));
        const candidate = extractGoogleClickFromCookieValue(decoded);
        if (candidate.gclid) return candidate;
      }
    }
  } catch (e) {
    try {
      console.warn('[Tracking] Failed to parse cookies for gclid', e?.message || String(e));
    } catch (_) {}
  }
  return { gclid: '', capturedAt: '' };
}

export function readGclidFromCookies() {
  return readGoogleClickFromCookies().gclid;
}

function readGaClientCookie() {
  if (!isDocument) return '';
  try {
    for (const entry of String(document.cookie || '').split(';')) {
      const [rawKey, ...rawValue] = entry.trim().split('=');
      if (rawKey === '_ga' && rawValue.length) {
        return extractGaClientIdFromCookieValue(decodeURIComponent(rawValue.join('=')));
      }
    }
  } catch (_) {}
  return '';
}

function readGtagField(measurementId, field, timeoutMs) {
  return new Promise((resolve) => {
    if (!isBrowser || typeof window.gtag !== 'function') {
      resolve('');
      return;
    }
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(String(value || '').trim());
    };
    const timeoutId = window.setTimeout(() => finish(''), timeoutMs);
    try {
      window.gtag('get', measurementId, field, finish);
    } catch (_) {
      finish('');
    }
  });
}

export async function readGoogleAnalyticsIdentifiers({
  measurementId = 'G-43KTYPJPNM',
  timeoutMs = 1500,
} = {}) {
  const normalizedMeasurementId = String(measurementId || '').trim();
  if (!normalizedMeasurementId) {
    return { clientId: '', sessionId: '', capturedAt: new Date().toISOString() };
  }
  const [gtagClientId, sessionId] = await Promise.all([
    readGtagField(normalizedMeasurementId, 'client_id', timeoutMs),
    readGtagField(normalizedMeasurementId, 'session_id', timeoutMs),
  ]);
  return {
    clientId: gtagClientId || readGaClientCookie(),
    sessionId: /^\d+$/.test(sessionId) ? sessionId : '',
    capturedAt: new Date().toISOString(),
  };
}

function ga4ItemText(value) {
  const normalized = String(value ?? '').trim();
  if (
    !normalized ||
    normalized.length > 100 ||
    /[\u0000-\u001f\u007f]/.test(normalized)
  ) {
    return '';
  }
  return normalized;
}

export function buildGa4PurchaseItems(items) {
  if (!Array.isArray(items) || !items.length) return [];
  const result = [];

  for (const item of items) {
    const itemId = ga4ItemText(item?.itemId);
    const itemName = ga4ItemText(item?.itemName);
    if (!itemId && !itemName) return [];

    const base = {};
    if (itemId) base.item_id = itemId;
    if (itemName) base.item_name = itemName;

    let variantRows = 0;
    const matrices = item?.sizeMatrices && typeof item.sizeMatrices === 'object'
      ? item.sizeMatrices
      : {};
    for (const [rawColor, matrix] of Object.entries(matrices)) {
      if (!matrix || typeof matrix !== 'object' || Array.isArray(matrix)) return [];
      const color = ga4ItemText(rawColor);
      if (!color) return [];
      for (const [rawSize, rawQuantity] of Object.entries(matrix)) {
        const size = ga4ItemText(rawSize);
        const quantity = Number(rawQuantity);
        if (!size || !Number.isSafeInteger(quantity) || quantity < 0) return [];
        if (quantity === 0) continue;
        const itemVariant = ga4ItemText(`${color} | ${size}`);
        if (!itemVariant) return [];
        result.push({
          ...base,
          item_variant: itemVariant,
          quantity,
        });
        variantRows += 1;
      }
    }
    if (variantRows) continue;

    const quantity = Number(item?.quantity);
    const colors = Array.isArray(item?.colors)
      ? item.colors.map(ga4ItemText).filter(Boolean)
      : [];
    if (
      !Number.isSafeInteger(quantity) ||
      quantity <= 0 ||
      colors.length > 1
    ) {
      return [];
    }
    const fallback = { ...base, quantity };
    if (colors.length === 1) fallback.item_variant = colors[0];
    result.push(fallback);
  }

  return result;
}

function safeReadJson(storage, key) {
  if (!storage || typeof storage.getItem !== 'function') return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {}
  return null;
}

function legacyRead(storage, key) {
  if (!storage || typeof storage.getItem !== 'function') return '';
  try {
    return storage.getItem(key) || '';
  } catch (_) {
    return '';
  }
}

export function normalizeGoogleClickSnapshot(data = {}) {
  const source = data && typeof data === 'object' ? data : {};
  const identifiers = {
    gclid: String(source.gclid || '').trim(),
    gbraid: String(source.gbraid || '').trim(),
    wbraid: String(source.wbraid || '').trim(),
  };
  const count = Object.values(identifiers).filter(Boolean).length;
  const conflict = source.googleClickIdConflict === true || count > 1;
  if (conflict) {
    return {
      ...source,
      gclid: '',
      gbraid: '',
      wbraid: '',
      googleClickIdConflict: true,
    };
  }
  return {
    ...source,
    ...identifiers,
    googleClickIdConflict: false,
  };
}

function boundedGoogleClickText(value, maxLength) {
  const normalized = String(value ?? '').trim();
  if (
    !normalized
    || normalized.length > maxLength
    || /[\u0000-\u001f\u007f]/.test(normalized)
  ) {
    return '';
  }
  return normalized;
}

function normalizedIso(value) {
  const parsed = Date.parse(String(value || '').trim());
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : '';
}

function candidateDedupeKey(candidate) {
  return [
    candidate.identifier_type,
    candidate.identifier,
    candidate.source,
    candidate.captured_at || '',
    candidate.version ?? '',
  ].join('\u001f');
}

function normalizeBrowserGoogleClickCandidate(raw, boundaryAt, observedAt) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const identifierType = boundedGoogleClickText(
    raw.identifier_type ?? raw.identifierType,
    20,
  ).toLowerCase();
  const identifier = boundedGoogleClickText(
    raw.identifier,
    MAX_GOOGLE_CLICK_IDENTIFIER_LENGTH,
  );
  const source = boundedGoogleClickText(
    raw.source,
    MAX_GOOGLE_CLICK_SOURCE_LENGTH,
  ).toLowerCase();
  if (!GOOGLE_CLICK_IDENTIFIER_TYPES.has(identifierType) || !identifier) return null;

  const captureVersion = Number(raw.version ?? raw.capture_version ?? raw.captureVersion);
  const version = Number.isSafeInteger(captureVersion) ? captureVersion : null;
  const capturedAt = normalizedIso(raw.captured_at ?? raw.capturedAt);
  const candidateObservedAt = normalizedIso(raw.observed_at ?? raw.observedAt) || observedAt;
  const parsedBoundary = Date.parse(boundaryAt);
  const parsedCapturedAt = Date.parse(capturedAt);

  let trustState = 'trusted';
  let ineligibleReason = null;
  if (!capturedAt) {
    trustState = 'ineligible';
    ineligibleReason = 'missing_capture_time';
  } else if (!source) {
    trustState = 'ineligible';
    ineligibleReason = 'missing_capture_source';
  } else if (!GOOGLE_CLICK_CAPTURE_SOURCES.has(source)) {
    trustState = 'ineligible';
    ineligibleReason = 'unsupported_capture_source';
  } else if (version === null) {
    trustState = 'ineligible';
    ineligibleReason = 'missing_capture_version';
  } else if (version !== GOOGLE_CLICK_CAPTURE_VERSION) {
    trustState = 'ineligible';
    ineligibleReason = 'unsupported_capture_version';
  } else if (parsedCapturedAt > parsedBoundary) {
    trustState = 'ineligible';
    ineligibleReason = 'future_capture_time';
  }

  return {
    identifier_type: identifierType,
    identifier,
    source,
    captured_at: capturedAt || null,
    observed_at: candidateObservedAt,
    version,
    eligible: trustState === 'trusted',
    trust_state: trustState,
    ineligible_reason: ineligibleReason,
  };
}

function legacyGoogleClickCandidates(value, observedAt) {
  if (!value || typeof value !== 'object') return [];
  const source = boundedGoogleClickText(
    value.clickCaptureSource
      ?? value.google_click_capture_source
      ?? value.captureSource,
    MAX_GOOGLE_CLICK_SOURCE_LENGTH,
  ).toLowerCase();
  const capturedAt = value.lastCapturedAt ?? value.google_click_captured_at ?? null;
  const version = value.clickCaptureVersion ?? value.google_click_capture_version ?? null;
  return ['gclid', 'gbraid', 'wbraid']
    .map((identifierType) => ({
      identifier_type: identifierType,
      identifier: value[identifierType],
      source: source || 'browser_storage_legacy',
      captured_at: capturedAt,
      observed_at: observedAt,
      version,
    }))
    .filter((candidate) => Boolean(String(candidate.identifier || '').trim()));
}

export function selectGoogleClickCandidateSnapshot(input = {}, {
  boundaryAt = new Date().toISOString(),
  observedAt = boundaryAt,
} = {}) {
  const normalizedBoundaryAt = normalizedIso(boundaryAt) || new Date().toISOString();
  const normalizedObservedAt = normalizedIso(observedAt) || normalizedBoundaryAt;
  const rawCandidates = Array.isArray(input?.google_click_candidates)
    ? input.google_click_candidates
    : (Array.isArray(input?.googleClickCandidates) ? input.googleClickCandidates : []);
  const candidateOverflow = input?.googleClickCandidateOverflow === true
    || input?.google_click_candidate_overflow === true
    || rawCandidates.length > MAX_GOOGLE_CLICK_CANDIDATES;
  const candidates = [];
  const seen = new Set();
  const candidateInputs = [
    ...rawCandidates,
    ...(rawCandidates.length ? [] : legacyGoogleClickCandidates(input, normalizedObservedAt)),
  ];
  for (const rawCandidate of candidateInputs) {
    const candidate = normalizeBrowserGoogleClickCandidate(
      rawCandidate,
      normalizedBoundaryAt,
      normalizedObservedAt,
    );
    if (!candidate) continue;
    const key = candidateDedupeKey(candidate);
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push(candidate);
  }
  const boundedCandidates = candidates.slice(0, MAX_GOOGLE_CLICK_CANDIDATES).map((candidate) => (
    candidateOverflow
      ? {
        ...candidate,
        eligible: false,
        trust_state: 'ineligible',
        ineligible_reason: 'candidate_set_too_large',
      }
      : candidate
  ));
  const trustedByIdentity = new Map();
  for (const candidate of boundedCandidates) {
    if (!candidate.eligible) continue;
    const identity = `${candidate.identifier_type}\u001f${candidate.identifier}`;
    const existing = trustedByIdentity.get(identity);
    if (!existing || candidate.captured_at < existing.captured_at) {
      trustedByIdentity.set(identity, candidate);
    }
  }
  const stableTrusted = [...trustedByIdentity.values()];
  const newestTimestamp = stableTrusted.reduce((latest, candidate) => (
    !latest || candidate.captured_at > latest ? candidate.captured_at : latest
  ), '');
  const newest = newestTimestamp
    ? stableTrusted.filter((candidate) => candidate.captured_at === newestTimestamp)
    : [];
  const identities = new Map(
    newest.map((candidate) => [
      `${candidate.identifier_type}\u001f${candidate.identifier}`,
      candidate,
    ]),
  );
  const newestIdentityCandidates = [...identities.values()];
  const newestTypes = new Set(
    newestIdentityCandidates.map((candidate) => candidate.identifier_type),
  );
  const coherentUrlIdentifierBundle = newestIdentityCandidates.length > 1
    && newestTypes.size === newestIdentityCandidates.length
    && newestIdentityCandidates.every((candidate) => (
      candidate.source === 'url_param'
      && candidate.version === GOOGLE_CLICK_CAPTURE_VERSION
    ));
  const selectedCandidates = identities.size === 1
    ? newestIdentityCandidates
    : (coherentUrlIdentifierBundle ? newestIdentityCandidates : []);
  const selected = selectedCandidates[0] || null;
  const explicitConflict = input?.googleClickIdConflict === true
    || input?.google_click_id_conflict === true;
  const allIdentities = new Set(
    boundedCandidates.map((candidate) => (
      `${candidate.identifier_type}\u001f${candidate.identifier}`
    )),
  );
  const unresolvedAmbiguous = !selectedCandidates.length && allIdentities.size > 1;
  let conflictReason = '';
  if (candidateOverflow) {
    conflictReason = 'candidate_set_too_large';
  } else if (identities.size > 1 && !coherentUrlIdentifierBundle) {
    conflictReason = 'equal_time_identity_conflict';
  } else if (unresolvedAmbiguous) {
    conflictReason = 'ambiguous_untrusted_click_ids';
  } else if (!selected && explicitConflict) {
    conflictReason = 'explicit_click_conflict';
  } else if (!selected && boundedCandidates.length) {
    const reasons = new Set(boundedCandidates.map((candidate) => candidate.ineligible_reason));
    conflictReason = reasons.has('future_capture_time')
      ? 'future_capture_time'
      : ([...reasons].filter(Boolean)[0] || 'untrusted_click_candidate');
  } else if (!selected) {
    conflictReason = 'missing_google_click_id';
  }

  const snapshot = {
    gclid: '',
    gbraid: '',
    wbraid: '',
    googleClickIdConflict: (identities.size > 1 && !coherentUrlIdentifierBundle)
      || unresolvedAmbiguous
      || (!selected && explicitConflict),
    lastCapturedAt: selected?.captured_at || '',
    clickCaptureVersion: selected?.version ?? null,
    clickCaptureSource: selected?.source || '',
    googleClickConflictReason: conflictReason,
    googleClickCandidateOverflow: candidateOverflow,
    google_click_candidates: boundedCandidates,
  };
  for (const candidate of selectedCandidates) {
    snapshot[candidate.identifier_type] = candidate.identifier;
  }
  return snapshot;
}

export function buildGoogleAdsSnapshot(
  storedGoogleAds = {},
  payloadGoogleAds = {},
  cookieGoogleClick = {},
) {
  const boundaryAt = new Date().toISOString();
  const storedCandidates = Array.isArray(storedGoogleAds?.google_click_candidates)
    ? storedGoogleAds.google_click_candidates
    : legacyGoogleClickCandidates(storedGoogleAds, boundaryAt);
  const payloadCandidates = Array.isArray(payloadGoogleAds?.google_click_candidates)
    ? payloadGoogleAds.google_click_candidates
    : legacyGoogleClickCandidates(payloadGoogleAds, boundaryAt);
  const existingCandidates = [...storedCandidates, ...payloadCandidates];
  const cookieGclid = String(cookieGoogleClick?.gclid || '').trim();
  const hasExistingTrustedCookieIdentity = existingCandidates.some((rawCandidate) => {
    const candidate = normalizeBrowserGoogleClickCandidate(
      rawCandidate,
      boundaryAt,
      boundaryAt,
    );
    return candidate?.eligible === true
      && candidate.identifier_type === 'gclid'
      && candidate.identifier === cookieGclid;
  });
  const cookieCandidate = cookieGclid && !hasExistingTrustedCookieIdentity
    ? [{
      identifier_type: 'gclid',
      identifier: cookieGclid,
      source: 'google_cookie',
      captured_at: cookieGoogleClick.capturedAt,
      observed_at: boundaryAt,
      version: GOOGLE_CLICK_CAPTURE_VERSION,
    }]
    : [];
  const selected = selectGoogleClickCandidateSnapshot({
    google_click_candidates: [
      ...existingCandidates,
      ...cookieCandidate,
    ],
    googleClickIdConflict: payloadGoogleAds?.googleClickIdConflict === true
      || storedGoogleAds?.googleClickIdConflict === true,
    googleClickCandidateOverflow: payloadGoogleAds?.googleClickCandidateOverflow === true
      || storedGoogleAds?.googleClickCandidateOverflow === true,
  }, { boundaryAt, observedAt: boundaryAt });
  const snapshot = {
    ...(storedGoogleAds || {}),
    ...(payloadGoogleAds || {}),
    ...selected,
  };
  snapshot.source = String(
    payloadGoogleAds?.source
    || storedGoogleAds?.source
    || selected.clickCaptureSource
    || '',
  ).trim();
  return snapshot;
}

export function mergeGoogleAdsUrlTracking(
  currentGoogleAds = {},
  search = '',
  capturedAt = '',
) {
  const params = new URLSearchParams(String(search || ''));
  const gclid = String(params.get('gclid') || '').trim();
  const gbraid = String(params.get('gbraid') || '').trim();
  const wbraid = String(params.get('wbraid') || '').trim();
  const campaign = String(params.get('utm_campaign') || params.get('campaign') || '').trim();
  const keyword = String(
    params.get('utm_term')
    || params.get('keyword')
    || params.get('searchkeyword')
    || '',
  ).trim();
  const utmSource = String(params.get('utm_source') || '').trim();
  const utmMedium = String(params.get('utm_medium') || '').trim();
  const boundaryAt = normalizedIso(capturedAt) || new Date().toISOString();
  const next = buildGoogleAdsSnapshot(currentGoogleAds, {}, {});
  let changed = false;
  let clickChanged = false;
  let clickCaptured = false;

  const urlClicks = [
    ['gclid', gclid],
    ['gbraid', gbraid],
    ['wbraid', wbraid],
  ].filter(([, value]) => Boolean(value));
  if (urlClicks.length) {
    const beforeKey = JSON.stringify(next.google_click_candidates || []);
    const existingIdentities = new Set(
      (next.google_click_candidates || []).map((candidate) => (
        `${candidate.identifier_type}\u001f${candidate.identifier}`
      )),
    );
    const newUrlClicks = urlClicks.filter(([identifierType, identifier]) => (
      !existingIdentities.has(`${identifierType}\u001f${identifier}`)
    ));
    const selected = selectGoogleClickCandidateSnapshot({
      google_click_candidates: [
        ...(next.google_click_candidates || []),
        ...newUrlClicks.map(([identifierType, identifier]) => ({
          identifier_type: identifierType,
          identifier,
          source: 'url_param',
          captured_at: capturedAt,
          observed_at: boundaryAt,
          version: GOOGLE_CLICK_CAPTURE_VERSION,
        })),
      ],
    }, { boundaryAt, observedAt: boundaryAt });
    const afterKey = JSON.stringify(selected.google_click_candidates || []);
    clickChanged = beforeKey !== afterKey
      || next.gclid !== selected.gclid
      || next.gbraid !== selected.gbraid
      || next.wbraid !== selected.wbraid
      || next.googleClickIdConflict !== selected.googleClickIdConflict;
    clickCaptured = newUrlClicks.length > 0
      && selected.clickCaptureSource === 'url_param'
      && selected.lastCapturedAt === boundaryAt;
    Object.assign(next, selected);
    if (clickChanged) changed = true;
  }

  for (const [key, value] of [
    ['campaign', campaign],
    ['keyword', keyword],
    ['utmSource', utmSource],
    ['utmMedium', utmMedium],
  ]) {
    if (value && next[key] !== value) {
      next[key] = value;
      changed = true;
    }
  }

  if (clickCaptured) next.source = 'url_param';
  return {
    googleAds: next,
    changed,
    clickChanged,
    clickCaptured,
  };
}

export function readStoredGoogleAds() {
  if (!isBrowser) return {};
  let data = null;
  try { data = safeReadJson(window.sessionStorage, 'printee:last-googleads'); } catch (_) {}
  if (!data) {
    try { data = safeReadJson(window.localStorage, 'printee:last-googleads'); } catch (_) {}
  }
  if (data) {
    if (!data.conversionTime) {
      let storedConversion = legacyRead(window.sessionStorage, 'printee:last-conversion-time');
      if (!storedConversion) storedConversion = legacyRead(window.localStorage, 'printee:last-conversion-time');
      if (storedConversion) data.conversionTime = storedConversion;
    }
    return buildGoogleAdsSnapshot(data, {}, {});
  }

  const fallback = {};
  let stored = legacyRead(window.sessionStorage, 'printee:last-gclid');
  if (!stored) stored = legacyRead(window.localStorage, 'printee:last-gclid');
  if (stored && typeof stored === 'string') fallback.gclid = stored.trim();
  let storedConversion = legacyRead(window.sessionStorage, 'printee:last-conversion-time');
  if (!storedConversion) storedConversion = legacyRead(window.localStorage, 'printee:last-conversion-time');
  if (storedConversion) fallback.conversionTime = storedConversion.trim();
  return buildGoogleAdsSnapshot(fallback, {}, {});
}

export function writeStoredGoogleAds(data, extras = {}) {
  if (!isBrowser || !data || typeof data !== 'object') return;
  const normalized = buildGoogleAdsSnapshot(data, {}, {});
  const payload = {
    gclid: normalized.gclid,
    gbraid: normalized.gbraid,
    wbraid: normalized.wbraid,
    googleClickIdConflict: normalized.googleClickIdConflict,
    campaign: (normalized.campaign || '').trim(),
    keyword: (normalized.keyword || '').trim(),
    utmSource: (normalized.utmSource || '').trim(),
    utmMedium: (normalized.utmMedium || '').trim(),
    captureSource: (normalized.source || '').trim(),
    lastCapturedAt: (normalized.lastCapturedAt || '').trim(),
    clickCaptureVersion: normalized.clickCaptureVersion,
    clickCaptureSource: (normalized.clickCaptureSource || '').trim(),
    googleClickConflictReason: (normalized.googleClickConflictReason || '').trim(),
    googleClickCandidateOverflow: normalized.googleClickCandidateOverflow === true,
    google_click_candidates: normalized.google_click_candidates || [],
    conversionTime: (extras.conversionTime || normalized.conversionTime || '').trim(),
  };
  const serialized = JSON.stringify(payload);
  try { window.sessionStorage?.setItem('printee:last-googleads', serialized); } catch (_) {}
  try { window.localStorage?.setItem('printee:last-googleads', serialized); } catch (_) {}

  if (payload.conversionTime) {
    try { window.sessionStorage?.setItem('printee:last-conversion-time', payload.conversionTime); } catch (_) {}
    try { window.localStorage?.setItem('printee:last-conversion-time', payload.conversionTime); } catch (_) {}
  }
  if (payload.gclid) {
    try { window.sessionStorage?.setItem('printee:last-gclid', payload.gclid); } catch (_) {}
    try { window.localStorage?.setItem('printee:last-gclid', payload.gclid); } catch (_) {}
  }
}

export function detectDeviceCategory() {
  if (typeof navigator === 'undefined') return '';
  try {
    const nav = navigator;
    if (nav.userAgentData && typeof nav.userAgentData === 'object') {
      if (nav.userAgentData.mobile) return 'mobile';
      const platform = String(nav.userAgentData.platform || '').toLowerCase();
      if (platform.includes('android')) return 'tablet';
    }
    const ua = String(nav.userAgent || '');
    const isTablet = /ipad|tablet|nexus 7|nexus 9|nexus 10|xoom|silk|sm-t|kfapwi|kindle/i.test(ua)
      || (/android/i.test(ua) && !/mobile/i.test(ua));
    if (isTablet) return 'tablet';
    const isMobile = /mobi|iphone|ipod|android.+mobile|blackberry|iemobile|opera mini/i.test(ua);
    if (isMobile) return 'mobile';
    return 'desktop';
  } catch (_) {
    return '';
  }
}

export function getGoogleAdsSnapshot(payloadGoogleAds = {}) {
  const stored = readStoredGoogleAds();
  const cookieClick = readGoogleClickFromCookies();
  return buildGoogleAdsSnapshot(stored, payloadGoogleAds, cookieClick);
}

export function getTrackingMetadataSnapshot(payloadMetadata = {}) {
  const stored = readStoredGoogleAds();
  const snapshot = { ...(payloadMetadata || {}) };
  if (!snapshot.conversionTime && stored?.conversionTime) {
    snapshot.conversionTime = stored.conversionTime;
  }
  if (!snapshot.device) snapshot.device = detectDeviceCategory();
  return snapshot;
}
